import { __rest } from '../../../../../node_modules/tslib/tslib.es6.mjs.js';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsHeatmap from 'highcharts/modules/heatmap';
import { cloneDeep, uniq, merge } from 'lodash';
import { classNames } from 'primereact/utils';
import React, { useRef, useEffect } from 'react';
import { DATA_TEST_KEYS } from '../../../../constants.js';
import { ColumnDataType } from '../../../../types.js';
import './C2Chart.css.js';
import { getXAxisDataColumn, getSeriesDataColumns, getValueDataColumn, heatmapMinMax, isDateType, isNumericType, isTextType, getHighchartsChartType } from './chartUtils.js';

HighchartsHeatmap(Highcharts);
const C2ChartChild = (_a) => {
    var _b;
    var { className, dataColumns = [], dataItems = [], loading, onLoad, chartVisualSqlQuery, initialized, isPreview, chartType, onLoadCounter } = _a, otherProps = __rest(_a, ["className", "dataColumns", "dataItems", "loading", "onLoad", "chartVisualSqlQuery", "initialized", "isPreview", "chartType", "onLoadCounter"]);
    const wrapper = useRef(null);
    const ref = useRef(null);
    const colors = (_b = otherProps.colors) !== null && _b !== void 0 ? _b : [
        getColorByName("indigo-500"),
        getColorByName("blue-500"),
        getColorByName("cyan-500"),
        getColorByName("pink-500"),
        getColorByName("orange-500"),
        getColorByName("green-500"),
        getColorByName("yellow-500"),
        getColorByName("red-500"),
        getColorByName("purple-500"),
        getColorByName("teal-500")
    ];
    const options = optionsFactory();
    useEffect(() => {
        if (typeof chartVisualSqlQuery === "undefined" || !onLoad) {
            return;
        }
        onLoad({ query: chartVisualSqlQuery });
    }, [chartVisualSqlQuery]);
    useEffect(() => {
        if (typeof chartVisualSqlQuery === "undefined") {
            // legacy
            onLoad === null || onLoad === void 0 ? void 0 : onLoad({ query: null });
        }
        else {
            onLoad === null || onLoad === void 0 ? void 0 : onLoad({ query: chartVisualSqlQuery });
        }
    }, []);
    useEffect(() => {
        if (typeof onLoadCounter !== "undefined" && onLoadCounter !== 0) {
            if (typeof chartVisualSqlQuery === "undefined") {
                onLoad === null || onLoad === void 0 ? void 0 : onLoad({
                    query: null
                }); // legacy
            }
            else {
                onLoad === null || onLoad === void 0 ? void 0 : onLoad({ query: chartVisualSqlQuery });
            }
        }
    }, [onLoadCounter]);
    useEffect(() => {
        if (wrapper.current) {
            const resizeObserver = new window.ResizeObserver(() => {
                var _a, _b;
                (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.chart) === null || _b === void 0 ? void 0 : _b.reflow();
            });
            resizeObserver.observe(wrapper.current);
            return () => {
                resizeObserver.disconnect();
            };
        }
    }, []);
    useEffect(() => {
        var _a;
        if (loading) {
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.chart.showLoading();
            return () => {
                var _a;
                (_a = ref.current) === null || _a === void 0 ? void 0 : _a.chart.hideLoading();
            };
        }
    }, [loading]);
    const dataTestName = otherProps[DATA_TEST_KEYS.NAME];
    const dataTestId = otherProps[DATA_TEST_KEYS.ID];
    const dataTestType = otherProps[DATA_TEST_KEYS.TYPE];
    function optionsFactory() {
        var _a, _b, _c, _d;
        if (!otherProps.options) {
            return;
        }
        const options = cloneDeep(otherProps.options);
        const type = getHighchartsChartType(chartType);
        if (type !== null) {
            options.chart = Object.assign({}, options.chart, { type });
        }
        const visibleDataColumns = dataColumns.filter((dataColumn) => dataColumn.show);
        if (visibleDataColumns.length === 0) {
            return options;
        }
        const xAxisDataColumn = getXAxisDataColumn(visibleDataColumns);
        if (typeof xAxisDataColumn === "undefined") {
            return options;
        }
        const seriesDataColumns = getSeriesDataColumns(visibleDataColumns);
        const valueColumn = getValueDataColumn(visibleDataColumns);
        if (chartType === "HeatmapChart") {
            const valuesAsNumbers = dataItems.flatMap((item) => {
                if (!valueColumn) {
                    return [];
                }
                const value = item[valueColumn.field];
                if (isNaN(Number(value))) {
                    return [];
                }
                return Number(value);
            });
            const yAxisColumn = seriesDataColumns.length > 0 ? seriesDataColumns[0] : undefined;
            if (typeof yAxisColumn === "undefined") {
                return options;
            }
            const xAxisCategories = uniq(dataItems.map((item) => item[xAxisDataColumn.field]));
            const yAxisCategories = uniq(dataItems.map((item) => item[yAxisColumn.field]));
            const { min, max } = heatmapMinMax(valuesAsNumbers);
            const heatmapDataItems = valueColumn === null
                ? []
                : xAxisCategories
                    .map((xCategory, xIndex) => {
                    return yAxisCategories.flatMap((yCategory, yIndex) => {
                        const item = dataItems.find((item) => item[xAxisDataColumn.field] === xCategory &&
                            item[yAxisColumn.field] === yCategory);
                        if (item && valueColumn) {
                            const value = item[valueColumn.field];
                            const converted = isNaN(Number(value))
                                ? undefined
                                : Number(value);
                            return [
                                {
                                    x: xIndex,
                                    y: yIndex,
                                    value: converted
                                }
                            ];
                        }
                        return [];
                    });
                })
                    .flat();
            options.chart = Object.assign({}, options.chart, {
                marginTop: 40,
                marginBottom: 80,
                plotBorderWidth: 1
            });
            options.xAxis.categories = xAxisCategories;
            options.yAxis.categories = yAxisCategories;
            options.legend = Object.assign({}, options.legend, {
                align: "right",
                layout: "vertical",
                verticalAlign: "top",
                y: 25,
                symbolHeight: 280
            });
            options.plotOptions = Object.assign({}, options.plotOptions);
            if (!options.plotOptions.series.dataLabels.color) {
                options.plotOptions.series.dataLabels.color = "#000000";
            }
            options.colorAxis = Object.assign({}, options.colorAxis, { min, max });
            if (!options.colorAxis.minColor) {
                options.colorAxis.minColor = "#FFFFFF";
            }
            if (!options.colorAxis.maxColor) {
                options.colorAxis.maxColor = "#3B82F6";
            }
            if (options.series.length === 0) {
                options.series[0] = {};
            }
            const series = options.series[0];
            series.name = (_a = valueColumn === null || valueColumn === void 0 ? void 0 : valueColumn.header) !== null && _a !== void 0 ? _a : "Data";
            series.color = "transparent";
            series.borderWidth = 1;
            series.data = heatmapDataItems;
            return options;
        }
        options.chart = merge({ spacing: 32 }, options.chart);
        options.plotOptions = merge({
            pie: {
                innerSize: "66%"
            }
        }, options.plotOptions);
        options.series = seriesDataColumns.map((dataColumn, dataColumnIndex) => {
            var _a, _b;
            const seriesColor = colors[dataColumnIndex % colors.length];
            return {
                color: seriesColor,
                data: dataItems
                    ? dataItems
                        .map((dataItem, dataItemIndex) => {
                        const color = chartType === "PieChart"
                            ? colors[dataItemIndex % colors.length]
                            : seriesColor;
                        const xValue = dataItem[xAxisDataColumn.field];
                        const yValue = dataItem[dataColumn.field];
                        if (!xValue || !yValue) {
                            return;
                        }
                        const y = isNaN(Number(yValue)) ? undefined : Number(yValue);
                        if (isDateType(xAxisDataColumn.type)) {
                            const x = new Date(xValue);
                            return { x, y, name: x.toLocaleDateString(), color };
                        }
                        else if (isNumericType(xAxisDataColumn.type)) {
                            const x = isNaN(Number(xValue)) ? undefined : Number(xValue);
                            return { x, y, name: x, color };
                        }
                        else if (isTextType(xAxisDataColumn.type) ||
                            xAxisDataColumn.type === ColumnDataType.Uuid) {
                            return { x: dataItemIndex, y, name: xValue, color };
                        }
                        else {
                            return { x: dataItemIndex, y, name: dataItemIndex, color };
                        }
                    })
                        .filter(Boolean)
                    : [],
                marker: {
                    symbol: "circle"
                },
                name: dataColumn.header,
                style: {
                    fontFamily: "Inter"
                },
                turboThreshold: 0,
                type: (_a = dataColumn.chartType) !== null && _a !== void 0 ? _a : options.chart.type,
                yAxis: (_b = dataColumn.yAxis) !== null && _b !== void 0 ? _b : 0
            };
        });
        options.legend = merge({
            align: "left",
            itemStyle: {
                fontFamily: "Inter",
                fontWeight: "normal"
            },
            y: 16
        }, (_b = options.legend) !== null && _b !== void 0 ? _b : { itemStyle: {} });
        options.xAxis = merge({
            gridLineWidth: 0,
            labels: { y: 32 },
            lineWidth: 0,
            tickWidth: 0,
            startOnTick: true // render labels from beginning of x axis
        }, (_c = options.xAxis) !== null && _c !== void 0 ? _c : {});
        options.xAxis.labels.formatter = function () {
            return this.value;
        };
        if (xAxisDataColumn.type === ColumnDataType.Date ||
            xAxisDataColumn.type === ColumnDataType.Time ||
            xAxisDataColumn.type === ColumnDataType.TimeWithTimeZone ||
            xAxisDataColumn.type === ColumnDataType.Timestamp ||
            xAxisDataColumn.type === ColumnDataType.TimestampWithTimeZone ||
            new RegExp(ColumnDataType.TimestampWithTimeZoneP).test(xAxisDataColumn.type)) {
            options.xAxis.type = "datetime";
            // format timestamps to locale date string in x axis labels
            options.xAxis.labels.formatter = function () {
                const date = new Date(this.value);
                if (date === null || date === void 0 ? void 0 : date.toLocaleDateString())
                    return new Date(this.value).toLocaleDateString();
                else
                    return this.value;
            };
        }
        else if (xAxisDataColumn.type === ColumnDataType.Varchar) {
            options.xAxis.labels.formatter = function () {
                return Number.isInteger(this.value) && dataItems[this.value]
                    ? dataItems[this.value][xAxisDataColumn.field]
                    : "";
            };
        }
        else if (new RegExp(ColumnDataType.VarcharP).test(xAxisDataColumn.type)) {
            options.xAxis.labels.formatter = function () {
                return Number.isInteger(this.value) && dataItems[this.value]
                    ? dataItems[this.value][xAxisDataColumn.field]
                    : "";
            };
        }
        else if (xAxisDataColumn.type === ColumnDataType.Uuid) {
            options.xAxis.labels.formatter = function () {
                return Number.isInteger(this.value) && dataItems[this.value]
                    ? dataItems[this.value][xAxisDataColumn.field]
                    : "";
            };
        }
        if (options.chart.type)
            options.yAxis = merge({
                gridLineDashStyle: "ShortDash",
                gridLineWidth: 2
            }, (_d = options.yAxis) !== null && _d !== void 0 ? _d : {});
        return options;
    }
    return (React.createElement("div", { className: classNames("c2-chart", className), ref: wrapper, "data-test-type": dataTestType, "data-test-name": dataTestName, "data-test-id": dataTestId, id: `cmp-${dataTestId}` },
        React.createElement(HighchartsReact, Object.assign({ highcharts: Highcharts }, otherProps, { options: options !== null && options !== void 0 ? options : {}, ref: ref }))));
};
function getColorByName(colorName) {
    return window
        .getComputedStyle(document.body)
        .getPropertyValue(`--${colorName}`)
        .trim();
}

export { C2ChartChild as default };
//# sourceMappingURL=C2ChartChild.js.map
